import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'components/core/Link';
import { H2, H3, H4 } from 'components/core/Typography';
import { Plus, Minus } from 'components/svg';
import ChevronRight from 'components/svg/ChevronRight';

import SearchPriceIcon from 'assets/images/homepage/searchPrice.svg';
import GarageIcon from 'assets/images/homepage/garage.svg';
import VehicleIcon from 'assets/images/homepage/vehicle.svg';

import './CityCard.scss';

export function CityCard({ city, state, url, content }) {
	const [expandedSections, setExpandedSections] = useState({
		amenities: false,
		units: false,
		vehicles: false,
	});

	const toggleSection = (section) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	const handleKeyDown = (event, section) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			toggleSection(section);
		}
	};

	return (
		<div className="city-card">
			<div className="city-card__header">
				<H2
					size="large"
					weight="ultraBold"
					className="city-card__title"
				>
					{city}, {state} Storage
				</H2>
			</div>
			<div className="city-card__content">
				<div className="city-card__accordion">
					<div
						className={`city-card__feature ${expandedSections.amenities ? 'city-card__feature--expanded' : ''}`}
						onClick={() => toggleSection('amenities')}
						onKeyDown={(e) => handleKeyDown(e, 'amenities')}
						role="button"
						tabIndex={0}
					>
						<div className="city-card__feature-content">
							<img
								src={SearchPriceIcon}
								alt="Storage Amenities"
								className="city-card__icon"
							/>
							<H3
								size="small"
								weight="ultraBold"
								className="city-card__feature-text"
							>
								Storage Amenities
							</H3>
						</div>
						<span className="city-card__expand">
							{expandedSections.amenities ? (
								<Minus
									width="14"
									height="3"
								/>
							) : (
								<Plus
									width="14"
									height="14"
								/>
							)}
						</span>
					</div>
					<div
						className={`city-card__accordion-content city-card__accordion-content--no-top-radius ${expandedSections.amenities ? 'city-card__accordion-content--visible' : 'city-card__accordion-content--hidden'}`}
					>
						{content?.amenities?.map((amenity, index) => (
							<div
								key={index}
								className="city-card__link-wrapper"
							>
								<Link
									href={amenity.href}
									className="city-card__link"
								>
									<H4 size="smaller" weight="ultraBold">{amenity.text}</H4>
								</Link>
								<ChevronRight />
							</div>
						))}
					</div>

					<div
						className={`city-card__feature ${expandedSections.units ? 'city-card__feature--expanded' : ''}`}
						onClick={() => toggleSection('units')}
						onKeyDown={(e) => handleKeyDown(e, 'units')}
						role="button"
						tabIndex={0}
					>
						<div className="city-card__feature-content">
							<img
								src={GarageIcon}
								alt="Unit Sizes"
								className="city-card__icon"
							/>
							<H3
								size="small"
								weight="ultraBold"
								className="city-card__feature-text"
							>
								Storage Unit Sizes
							</H3>
						</div>
						<span className="city-card__expand">
							{expandedSections.units ? (
								<Minus
									width="14"
									height="3"
								/>
							) : (
								<Plus
									width="14"
									height="14"
								/>
							)}
						</span>
					</div>
					<div
						className={`city-card__accordion-content city-card__accordion-content--no-top-radius ${expandedSections.units ? 'city-card__accordion-content--visible' : 'city-card__accordion-content--hidden'}`}
					>
						{content?.unitTypes?.map((unit, index) => (
							<div
								key={index}
								className="city-card__link-wrapper"
							>
								<Link
									href={unit.href}
									className="city-card__link"
								>
									<H4 size="smaller" weight="ultraBold">{unit.text}</H4>
								</Link>
								<ChevronRight />
							</div>
						))}
					</div>

					<div
						className={`city-card__feature ${expandedSections.vehicles ? 'city-card__feature--expanded' : ''}`}
						onClick={() => toggleSection('vehicles')}
						onKeyDown={(e) => handleKeyDown(e, 'vehicles')}
						role="button"
						tabIndex={0}
					>
						<div className="city-card__feature-content">
							<img
								src={VehicleIcon}
								alt="Vehicle Storage"
								className="city-card__icon"
							/>
							<H3
								size="small"
								weight="ultraBold"
								className="city-card__feature-text"
							>
								Vehicle Storage
							</H3>
						</div>
						<span className="city-card__expand">
							{expandedSections.vehicles ? (
								<Minus
									width="14"
									height="3"
								/>
							) : (
								<Plus
									width="14"
									height="14"
								/>
							)}
						</span>
					</div>
					<div
						className={`city-card__accordion-content city-card__accordion-content--no-top-radius ${expandedSections.vehicles ? 'city-card__accordion-content--visible' : 'city-card__accordion-content--hidden'}`}
					>
						{content?.vehicleStorage?.map((vehicle, index) => (
							<div
								key={index}
								className="city-card__link-wrapper"
							>
								<Link
									href={vehicle.href}
									className="city-card__link"
								>
									<H4 size="smaller" weight="ultraBold">{vehicle.text}</H4>
								</Link>
								<ChevronRight />
							</div>
						))}
					</div>
				</div>
			</div>
			<Link
				href={url}
				className="city-card__cta"
			>
				<span className="city-card__cta-text">
					View all storage units in <br className="city-card__cta-break" />
					{city}, {state}
				</span>
			</Link>
		</div>
	);
}

CityCard.propTypes = {
	city: PropTypes.string.isRequired,
	state: PropTypes.string,
	url: PropTypes.string.isRequired,
	content: PropTypes.shape({
		vehicleStorage: PropTypes.arrayOf(
			PropTypes.shape({
				href: PropTypes.string.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
		amenities: PropTypes.arrayOf(
			PropTypes.shape({
				href: PropTypes.string.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
		unitTypes: PropTypes.arrayOf(
			PropTypes.shape({
				href: PropTypes.string.isRequired,
				text: PropTypes.string.isRequired,
			}),
		),
	}),
};

export default CityCard;
